import { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  FilePlus2, FolderKanban, Layers, BarChart3, Users, Globe,
  Settings as SettingsIcon, LogOut, ImagePlus, ShieldCheck, ChevronRight, FileText, ScanLine,
} from "lucide-react";

const navItem = (active) =>
  `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
    active
      ? "bg-indigo-600/15 text-indigo-300 border border-indigo-500/30 font-semibold"
      : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/60 border border-transparent"
  }`;

export default function Layout({ children }) {
  const { user, logout, isAdmin, hasModule } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const groups = [
    {
      key: "website", label: "Website Generator", icon: Globe, show: hasModule("website_generator"),
      items: [
        { to: "/create", label: "New Website", icon: FilePlus2, id: "new-website" },
        { to: "/create/national", label: "New National Website", icon: Globe, id: "new-national-website" },
        { to: "/websites", label: "My Websites", icon: FolderKanban, id: "my-websites" },
        ...(isAdmin ? [{ to: "/profiles", label: "Category Profiles", icon: Layers, id: "category-profiles" }] : []),
      ],
    },
    {
      key: "image", label: "Image Generation", icon: ImagePlus, show: hasModule("image_generator"),
      items: [
        { to: "/images", label: "Image Generator", icon: ImagePlus, id: "image-generator" },
        { to: "/streetview", label: "Street View Cleaner", icon: ScanLine, id: "streetview-cleaner" },
      ],
    },
    {
      key: "docs", label: "Documentation", icon: FileText, show: hasModule("document_editor"),
      items: [{ to: "/documents", label: "Documentation", icon: FileText, id: "documentation" }],
    },
    {
      key: "admin", label: "Admin", icon: ShieldCheck, show: isAdmin,
      items: [
        { to: "/activity", label: "Activity & Reports", icon: BarChart3, id: "activity-reports" },
        { to: "/team", label: "Team", icon: Users, id: "team" },
        { to: "/settings", label: "Settings", icon: SettingsIcon, id: "settings" },
      ],
    },
  ].filter((g) => g.show);

  const inGroup = (g) => g.items.some((it) => location.pathname === it.to || location.pathname.startsWith(it.to + "/"));
  const [open, setOpen] = useState(() => {
    const o = {};
    groups.forEach((g) => { o[g.key] = inGroup(g); });
    if (!Object.values(o).some(Boolean) && groups[0]) o[groups[0].key] = true;
    return o;
  });
  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));

  return (
    <div className="flex min-h-screen bg-[#0B0F17]">
      <aside className="w-64 shrink-0 bg-slate-950/95 backdrop-blur-md border-r border-slate-800/80 flex flex-col fixed h-screen">
        <div className="h-16 flex items-center px-6 border-b border-slate-800/80">
          <div data-testid="brand-wordmark"
            className="text-xl font-black tracking-tight bg-gradient-to-r from-slate-100 via-indigo-200 to-indigo-400 bg-clip-text text-transparent font-h">
            OpusOS
          </div>
          <span className="ml-2 mt-0.5 text-[9px] font-mono uppercase tracking-widest text-slate-500">Ops Hub</span>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-2 overflow-y-auto">
          {groups.map((g) => {
            const isOpen = !!open[g.key];
            const active = inGroup(g);
            return (
              <div key={g.key}>
                <button
                  data-testid={`sidebar-group-${g.key}`}
                  onClick={() => toggle(g.key)}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border text-sm font-semibold transition-colors ${
                    active
                      ? "bg-indigo-600/10 border-indigo-500/30 text-indigo-200"
                      : "bg-slate-900/60 border-slate-800 text-slate-200 hover:border-slate-700"
                  }`}
                >
                  <span className="flex items-center gap-2.5">
                    <g.icon className="w-4 h-4 text-indigo-400" /> {g.label}
                  </span>
                  <ChevronRight className={`w-4 h-4 text-slate-500 transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`} />
                </button>
                {isOpen && (
                  <div className="mt-1 ml-3 pl-3 border-l border-slate-800/80 space-y-1 py-1 animate-fade-up" data-testid={`sidebar-items-${g.key}`}>
                    {g.items.map((it) => (
                      <NavLink key={it.id} to={it.to} end data-testid={`sidebar-nav-${it.id}`} className={({ isActive }) => navItem(isActive)}>
                        <it.icon className="w-4 h-4" /> {it.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="border-t border-slate-800/80 p-4">
          <div className="min-w-0 mb-3">
            <div className="text-sm text-slate-200 font-medium truncate" data-testid="current-user-email">{user?.email}</div>
            <span className={`inline-block mt-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded ${
              isAdmin ? "bg-amber-500/15 text-amber-300 border border-amber-500/30" : "bg-slate-800 text-slate-400 border border-slate-700"
            }`} data-testid="current-user-role">{user?.role}</span>
          </div>
          <button data-testid="logout-btn" onClick={async () => { await logout(); navigate("/login"); }}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm text-slate-400 hover:text-slate-100 hover:bg-slate-800/60 transition-colors font-h">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 ml-64 min-h-screen">
        <div className="p-6 lg:p-8 max-w-7xl mx-auto">{children}</div>
      </main>
    </div>
  );
}
